// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Broker } from '@shared/data-access/interfaces/api/brokerage/account';
import { type BrokerCredentialsResp } from '@shared/data-access/interfaces/api/brokerage/commons';
import { brokerAuthorizationPopUp } from '@advisor-app/features/brokers/utils/broker-authorization-popup';
import { appStore as store } from '@advisor-app/store/store';
import { accountApi } from '@advisor-app/store/api/brokerage/accounts-api';
import { environment } from '@shared/environment';

export interface AuthorizationToken {
  code: string;
  state: string;
}

export interface IBKRAuthorizationToken {
  oauth_token: string;
  oauth_verifier: string;
}

export interface BrokerAuthPopupResponse {
  token: string;
  oauth_verifier?: string;
}

interface BrokerAuth {
  tradier: (state: string) => Promise<BrokerAuthPopupResponse>;
  alpaca: (state: string) => Promise<BrokerAuthPopupResponse>;
  interactive_brokers: () => Promise<BrokerAuthPopupResponse>;
}

export const brokerAuthenticationApi: BrokerAuth = {
  tradier: async state => {
    const params = {
      state,
      client_id: environment.BROKERS.TRADIER.CLIENT_ID!,
      scope: environment.BROKERS.TRADIER.SCOPE,
    };

    const queryParams = new URLSearchParams(params);
    const codeUri = `${environment.BROKERS.TRADIER.OAUTH_URL}?${queryParams}`;

    const response = <AuthorizationToken>await brokerAuthorizationPopUp(codeUri);

    return { token: response.code || '' };
  },

  alpaca: async state => {
    const params = {
      state,
      client_id: environment.BROKERS.ALPACA.CLIENT_ID!,
      scope: environment.BROKERS.ALPACA.SCOPE,
      redirect_uri: environment.BROKERS.ALPACA.REDIRECT_URL!,
      response_type: 'code',
    };

    const queryParams = new URLSearchParams(params);
    const codeUri = `${environment.BROKERS.ALPACA.OAUTH_URL}?${queryParams}`;
    const response = <AuthorizationToken>await brokerAuthorizationPopUp(codeUri);

    return { token: response.code || '' };
  },

  interactive_brokers: async () => {
    const resp = (await store.dispatch(
      accountApi.endpoints.postBrokerCredentials.initiate({
        brokerKey: Broker.IBKR,
        brokerCredentials: { token: '' },
      }),
    )) as unknown as { data: BrokerCredentialsResp };

    const params = { oauth_token: resp.data.token };
    const codeUri = `${
      environment.BROKERS.INTERACTIVE_BROKERS.OAUTH_URL
    }?${new URLSearchParams(params)}`;

    const response = <IBKRAuthorizationToken>(
      await brokerAuthorizationPopUp(codeUri)
    );

    return {
      token: response.oauth_token,
      oauth_verifier: response.oauth_verifier,
    };
  },
};
